import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import PhoneNumberModule from '../modules/phone-number/PhoneNumberModule.jsx';
import { ModuleContextProvider } from '../modules/context/ModuleContextProvider.jsx';

const regions = {
  US: [
    'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID',
    'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC',
    'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
    'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
  ],
  CA: [
    'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
  ]
};

export default function PhoneNumbers() {
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const [activeTab, setActiveTab] = useState('owned'); // 'owned' or 'search'
  const [ownedNumbers, setOwnedNumbers] = useState([]);
  const [searchParams, setSearchParams] = useState({
    areaCode: '',
    contains: '',
    inLocality: '',
    inRegion: '',
    inPostalCode: '',
    countryCode: '',
  });
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState({});
  const [error, setError] = useState(null);
  const [currentRegions, setCurrentRegions] = useState([]);

  // Load owned numbers on component mount
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      loadOwnedNumbers();
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    if (searchParams.countryCode) {
      setCurrentRegions(regions[searchParams.countryCode]);
    } else {
      setCurrentRegions([]);
    }
    setSearchParams(prev => ({ ...prev, inRegion: '' }));
  }, [searchParams.countryCode]);

  const loadOwnedNumbers = async () => {
    try {
      const data = await apiClient.get('/api/phone/numbers');
      setOwnedNumbers(data);
    } catch (err) {
      console.error('Failed to load owned numbers:', err);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(searchParams).filter(([, value]) => value)
      );
      const query = new URLSearchParams(filteredParams).toString();
      const data = await apiClient.get(`/api/phone/search?${query}`);
      setAvailableNumbers(data);
      if (data.length === 0) {
        setError('No numbers found matching your criteria.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (number) => {
    setError(null);
    try {
      await apiClient.post('/api/phone/purchase', { phoneNumber: number.phoneNumber });
      setSelectedNumber(number);
      // Refresh owned numbers list
      loadOwnedNumbers();
      // Switch to owned numbers tab
      setActiveTab('owned');
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleRelease = async (phoneNumberId) => {
    if (!confirm('Are you sure you want to release this phone number? This action cannot be undone and will stop monthly charges.')) {
      return;
    }

    setReleaseLoading(prev => ({ ...prev, [phoneNumberId]: true }));
    setError(null);
    
    try {
      await apiClient.delete(`/api/phone/numbers/${phoneNumberId}/release`);
      
      // Refresh owned numbers list
      loadOwnedNumbers();
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setReleaseLoading(prev => ({ ...prev, [phoneNumberId]: false }));
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Phone Numbers</h1>
        <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Purchase and manage your phone numbers</p>
        

      </div>
      
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('owned')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'owned'
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              My Numbers ({ownedNumbers.length})
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Buy New Number
            </button>
          </nav>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-500 text-red-800 dark:text-red-200 rounded-lg">
          {error}
        </div>
      )}

      {/* Owned Numbers Tab */}
      {activeTab === 'owned' && (
        <div>
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Your Phone Numbers</h2>
          {ownedNumbers.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg text-center border border-gray-200 dark:border-gray-700">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4 dark:text-gray-300 dark:text-gray-600">You don't have any phone numbers yet.</p>
              <button
                onClick={() => setActiveTab('search')}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors dark:text-white"
              >
                Buy Your First Number
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
              {ownedNumbers.map((number, index) => (
                <div key={number.id} className={`flex justify-between items-center p-4 ${index > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}>
                  <div>
                    <p className="font-mono text-lg text-gray-900 dark:text-white">{number.friendly_name || number.number}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      {number.country} • Area Code: {number.area_code}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Purchased: {new Date(number.purchased_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRelease(number.id)}
                    disabled={releaseLoading[number.id]}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors dark:text-white"
                  >
                    {releaseLoading[number.id] ? 'Releasing...' : 'Release'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Numbers Tab */}
      {activeTab === 'search' && (
        <div>
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Buy a New Phone Number</h2>
          
          <ModuleContextProvider>
            <PhoneNumberModule
              config={{
                context: 'dashboard',
                searchFilters: [
                  { type: 'country', enabled: true, required: false, defaultValue: 'US' },
                  { type: 'area-code', enabled: true, required: false },
                  { type: 'city', enabled: true, required: false },
                  { type: 'region', enabled: true, required: false },
                  { type: 'pattern', enabled: true, required: false }
                ],
                purchaseFlow: 'confirmation',
                showSkipOption: false,
                billingContext: {
                  source: 'dashboard',
                  category: 'phone-number-purchase'
                },
                onPurchaseComplete: (result) => {
                  console.log('Phone number purchased:', result);
                  if (!result.skipped) {
                    setSelectedNumber(result);
                    loadOwnedNumbers(); // Refresh the owned numbers list
                    setActiveTab('owned'); // Switch to owned numbers tab
                  }
                }
              }}
              onEvent={(event) => {
                console.log('PhoneNumber event:', event);
                if (event.type === 'PURCHASE_COMPLETED') {
                  // Additional handling if needed
                }
              }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700"
            />
          </ModuleContextProvider>

          {selectedNumber && !selectedNumber.skipped && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-500 text-green-800 dark:text-green-200 rounded-lg">
              <p>Congratulations! You have purchased {selectedNumber.friendlyName || selectedNumber.phoneNumber}.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
